import React from 'react';
import { styled, useTheme } from '@mui/material';

import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

const StyGenericSwitchBarWrapper = styled('div', {
    shouldForwardProp: (prop) => prop !== 'warningMessage',
})<
    React.HTMLAttributes<HTMLDivElement> & {
        warningMessage?: string;
    }
>(({ theme, warningMessage }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.primary.background,
    borderRadius: theme.spacing(1),
    ...(warningMessage && {
        borderColor: theme.palette.orange,
        borderStyle: 'solid',
        borderWidth: theme.spacing(0.25),
    }),
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
}));

type GenericSwitchBarProps = {
    checked: boolean;
    'data-testid'?: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    tooltipContent?: React.ReactNode;
    title: string;
    warningMessage?: string;
};

const GenericSwitchBar = ({
    checked,
    children,
    'data-testid': dataTestId,
    onChange,
    tooltipContent = null,
    title,
    warningMessage = undefined,
}: React.PropsWithChildren<GenericSwitchBarProps>): JSX.Element => {
    const theme = useTheme();
    return (
        <StyGenericSwitchBarWrapper warningMessage={warningMessage}>
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <div style={{ alignItems: 'center', display: 'flex' }}>
                    <Typography
                        sx={{ color: theme.palette.primary.main }}
                        variant="body1Bold"
                    >
                        {title}
                    </Typography>
                    {tooltipContent && (
                        <Tooltip title={tooltipContent}>
                            <InfoIcon
                                color="primary"
                                fontSize="medium"
                                sx={{ margin: `0 ${theme.spacing(1)}` }}
                            />
                        </Tooltip>
                    )}
                    {warningMessage && (
                        <Tooltip title={warningMessage}>
                            <WarningIcon color="warning" fontSize="medium" />
                        </Tooltip>
                    )}
                </div>
                <Switch
                    checked={checked}
                    data-testid={dataTestId}
                    onChange={onChange}
                />
            </div>
            {children}
        </StyGenericSwitchBarWrapper>
    );
};

export default GenericSwitchBar;
